import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import {
  TeamMembersFilterSheet,
  hasActiveTeamFilters,
  countActiveTeamFilters,
  EMPTY_TEAM_FILTERS,
} from '@/components/Pages/TeamMembersFilterSheet';
import type { TeamMembersFilters } from '@/components/Pages/TeamMembersFilterSheet';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Search,
  Users,
  Mail,
  Phone,
  Building2,
  Briefcase,
  UserCheck,
  UserX,
  Filter,
  LayoutGrid,
  LayoutList,
  Settings2,
  Eye,
  AlertCircle,
  MapPin,
  X,
} from 'lucide-react';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import { PageHeader } from '@/components/blocks/page-header';
import { EmployeeAvatar } from '@/components/EmployeeAvatar';
import { EmployeeImage } from '@/components/EmployeeImage';
import { EmployeeCardImage } from '@/components/EmployeeCardImage';
import { EmployeeGradeBadge } from '@/components/EmployeeGradeBadge';
import { TeamMemberDetailsDialog } from '@/components/TeamMemberDetailsDialog';
import { BulkSelectionBar } from '@/components/BulkSelectionBar';
import { BulkUpdateDialog } from '@/components/BulkUpdateDialog';
import { initializeMockData } from '@/data/mockData';
import type { Employee, WorkingPattern } from '@/types/timesheet';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import { useDataSource } from '@/contexts/DataSourceContext';
import { employeesApi, type EmployeeDirectorySummary } from '@/services/employeesApi';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { usePagination } from '@/hooks/usePagination';
import { calculatePagination } from '@/types/pagination';
import { usePermissions } from '@/contexts/PermissionContext';
import { useTimesheet } from '@/contexts/TimesheetContext';

// Extended Employee type with status
interface TeamMember extends Employee {
  status: 'active' | 'inactive' | 'on-leave';
  phone?: string;
  location?: string;
  startDate?: Date;
}

const getDemoPhotoUrl = (name: string, id: number): string => {
  const seed = encodeURIComponent(name.replace(/\s+/g, '-'));
  return `https://picsum.photos/seed/${seed}-${id}/300/300`;
};

const isEvoAvatarUrl = (url: string): boolean => {
  const trimmedUrl = url.trim().toLowerCase();
  if (trimmedUrl.includes('dicebear')) {
    return false;
  }
  return true;
};

const normalizeStatus = (status?: string): 'active' | 'inactive' | 'on-leave' => {
  const lower = status?.toLowerCase();
  if (lower === 'active') return 'active';
  if (lower === 'inactive') return 'inactive';
  if (lower === 'on-leave') return 'on-leave';
  return 'active';
};

const getPhotoUrl = (name: string, id: number, photoUrl?: string | null): string => {
  if (photoUrl && photoUrl.trim().length > 0 && isEvoAvatarUrl(photoUrl)) {
    return photoUrl;
  }
  return getDemoPhotoUrl(name, id);
};

// Returns the real Employee.PhotoUrl if available, undefined otherwise.
// Used in live mode so components fall back to initials instead of fake avatars.
const getLivePhotoUrl = (photoUrl?: string | null): string | undefined => {
  if (photoUrl && photoUrl.trim().length > 0 && isEvoAvatarUrl(photoUrl)) {
    return photoUrl;
  }
  return undefined;
};

export const TeamMembersPage: React.FC = () => {
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('team-members');
  const {
    canViewEmployeeDirectory,
    canViewEmployeeDetails,
    getEmployeeDirectoryViewAccessLevel,
    canManageCompanies,
    canManageApprovalWorkflows,
  } = usePermissions();
  const canBulkManageCompanyAccess = canManageCompanies();
  const canBulkManageApprovalWorkflow = canManageApprovalWorkflows();
  const { currentEmployee } = useTimesheet();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiTeamMembers, setApiTeamMembers] = useState<TeamMember[]>([]);
  const [totalApiMembers, setTotalApiMembers] = useState(0);
  const [directorySummary, setDirectorySummary] = useState<EmployeeDirectorySummary | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const { currentPage, pageSize, setPage, setPageSize, resetPagination } = usePagination();
  const [teamFilters, setTeamFilters] = useState<TeamMembersFilters>(EMPTY_TEAM_FILTERS);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [sortBy, setSortBy] = useState<string>('name');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  // --- Bulk selection ---
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [clearSelectionKey, setClearSelectionKey] = useState(0);
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [bulkUpdateInitialTab, setBulkUpdateInitialTab] = useState<'company-access' | 'approval-workflow'>('company-access');

    const initialMockTeamMembers: TeamMember[] = useMemo(() => {
      const mockData = initializeMockData();
      const grades: { name: string; level: number }[] = [
        { name: 'Junior', level: 1 },
        { name: 'Mid-Level', level: 2 },
        { name: 'Senior', level: 3 },
        { name: 'Lead', level: 4 },
        { name: 'Principal', level: 5 },
        { name: 'Director', level: 5 },
      ];
      
      // User roles from Settings > Roles & Permissions
      const userRoles = [
        { id: 1, name: 'Administrator' },
        { id: 2, name: 'User' },
        { id: 3, name: 'Approver' },
        { id: 4, name: 'Finance Manager' },
      ];
      
      return mockData.employees.map((emp, index) => {
        // Assign grade based on role
        let grade: typeof grades[number];
        const roleLower = emp.role.toLowerCase();
        if (roleLower.includes('junior') || roleLower.includes('graduate')) {
          grade = grades[0]; // Junior
        } else if (roleLower.includes('senior')) {
          grade = grades[2]; // Senior
        } else if (roleLower.includes('lead')) {
          grade = grades[3]; // Lead
        } else if (roleLower.includes('principal')) {
          grade = grades[4]; // Principal
        } else if (roleLower.includes('director') || roleLower.includes('head')) {
          grade = grades[5]; // Director
        } else {
          grade = grades[1]; // Mid-Level
        }
        
        // Assign user role based on job role
        let userRole = userRoles[1]; // Default to 'User'
        if (roleLower.includes('director') || roleLower.includes('head') || roleLower.includes('cto') || roleLower.includes('ceo')) {
          userRole = userRoles[0]; // Administrator
        } else if (roleLower.includes('manager') || roleLower.includes('lead')) {
          userRole = userRoles[2]; // Approver
        } else if (roleLower.includes('finance') || roleLower.includes('accountant')) {
          userRole = userRoles[3]; // Finance Manager
        }
        
        // Resolve manager name (find manager by ID)
        const manager = emp.managerId ? mockData.employees.find(e => e.id === emp.managerId) : null;
        
        return {
          ...emp,
          photoUrl: getPhotoUrl(emp.name, emp.id, emp.photoUrl),
          status: Math.random() > 0.1 ? 'active' : 'inactive', // 90% active
          phone: `+44 ${Math.floor(Math.random() * 9000) + 1000} ${Math.floor(Math.random() * 900000) + 100000}`,
          location: ['London', 'Manchester', 'Birmingham', 'Leeds', 'Bristol', 'Remote'][Math.floor(Math.random() * 6)],
          startDate: new Date(2020 + Math.floor(Math.random() * 4), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1),
          grade: grade.name,
          gradeLevel: grade.level,
          managerName: manager?.name,
          userRoleId: userRole.id,
          userRoleName: userRole.name,
          publicHolidayCountry: null,
        };
      });
    }, []);

  const [mockTeamMembers, setMockTeamMembers] = useState<TeamMember[]>(initialMockTeamMembers);

  const teamMembersRaw = screenMode === 'live' ? apiTeamMembers : mockTeamMembers;

  // Pre-filter members based on the employee directory ViewAccess permission level.
  // In live mode the backend GET /employees endpoint already applies ViewAccess
  // scoping, so we skip the client-side filter to avoid double-filtering issues
  // (e.g. when the platform user ID cannot be resolved to an employee ID on the
  // backend, the server falls back to returning all employees and the redundant
  // client filter would incorrectly narrow the list to zero).
  const teamMembers = useMemo(() => {
    if (screenMode === 'live') {
      return teamMembersRaw;
    }
    const accessLevel = getEmployeeDirectoryViewAccessLevel();
    switch (accessLevel) {
      case 'own':
        return teamMembersRaw.filter(m => m.id === currentEmployee?.id);
      case 'team':
        if (!currentEmployee?.department) return teamMembersRaw.filter(m => m.id === currentEmployee?.id);
        return teamMembersRaw.filter(m =>
          m.department?.toLowerCase() === currentEmployee.department?.toLowerCase()
        );
      case 'all':
      default:
        return teamMembersRaw;
    }
  }, [teamMembersRaw, screenMode, getEmployeeDirectoryViewAccessLevel, currentEmployee]);

  // Debounce the search box so typing does not fire a request per keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Searching, filtering or re-sorting always starts again from the first page
  const isFirstQueryRender = useRef(true);
  useEffect(() => {
    if (isFirstQueryRender.current) {
      isFirstQueryRender.current = false;
      return;
    }
    resetPagination();
  }, [debouncedSearchQuery, teamFilters, sortBy, resetPagination]);

  const fetchTeamMembers = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await employeesApi.getEmployees({
        page: currentPage,
        pageSize,
        search: debouncedSearchQuery || undefined,
        sortBy,
        sortOrder: 'asc',
        department: teamFilters.departments.join(',') || undefined,
        role: teamFilters.roles.join(',') || undefined,
        status: teamFilters.statuses.join(',') || undefined,
        type: teamFilters.types.join(',') || undefined,
      });
      if (signal?.aborted) return;

      const enhancedEmployees: TeamMember[] = response.data.map((emp) => ({
        ...emp,
        photoUrl: getLivePhotoUrl(emp.photoUrl),
        status: normalizeStatus(emp.status),
        phone: emp.phone || '',
        location: emp.location || '',
        startDate: emp.startDate ? new Date(emp.startDate) : new Date(),
        grade: emp.grade,
        gradeLevel: emp.gradeLevel,
        managerName: emp.managerName,
      }));

      setApiTeamMembers(enhancedEmployees);
      setTotalApiMembers(response.pagination.totalItems);
    } catch (err) {
      if (signal?.aborted) return;
      console.error('Error fetching team members:', err);
      setError(err instanceof Error ? err.message : 'Failed to load team members');
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearchQuery, sortBy, teamFilters]);

  // Organisation-wide counts and filter options, so the metric cards and the
  // filter drawer are not limited to the employees on the current page.
  const fetchDirectorySummary = useCallback(async (signal?: AbortSignal) => {
    try {
      const summary = await employeesApi.getDirectorySummary();
      if (signal?.aborted) return;
      setDirectorySummary(summary);
    } catch (err) {
      console.error('Error fetching employee directory summary:', err);
    }
  }, []);

  useEffect(() => {
    if (screenMode !== 'live') return;
    fetchTeamMembers();
  }, [screenMode, fetchTeamMembers]);

  useEffect(() => {
    if (screenMode !== 'live') return;
    fetchDirectorySummary();
  }, [screenMode, fetchDirectorySummary]);

  // Listen for company-changed event to refetch data when switching companies.
  // Debounced to coalesce rapid switches and avoid stale-context 403s.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleCompanyChange = (event: Event) => {
      clearTimeout(debounceTimer);
      const signal = (event as CustomEvent).detail?.signal as AbortSignal | undefined;

      debounceTimer = setTimeout(async () => {
        if (signal?.aborted) return;
        if (screenMode === 'live') {
          await Promise.all([fetchTeamMembers(signal), fetchDirectorySummary(signal)]);
        }
      }, 150);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [screenMode, fetchTeamMembers, fetchDirectorySummary]);

  // Get unique departments and roles for filters. In live mode these come from the
  // directory summary because the loaded members are only the current page.
  const departments = useMemo(() => {
    if (screenMode === 'live') {
      return (directorySummary?.departments ?? []).map(d => d.value).filter(Boolean).sort();
    }
    return Array.from(new Set(teamMembers.map(m => m.department).filter(Boolean))).sort();
  }, [screenMode, directorySummary, teamMembers]);

  const roles = useMemo(() => {
    if (screenMode === 'live') {
      return (directorySummary?.roles ?? []).map(r => r.value).filter(Boolean).sort();
    }
    return Array.from(new Set(teamMembers.map(m => m.role).filter(Boolean))).sort();
  }, [screenMode, directorySummary, teamMembers]);

  const statuses = useMemo(() => {
    if (screenMode === 'live') {
      return Array.from(
        new Set((directorySummary?.statuses ?? []).map(s => normalizeStatus(s.value)))
      ).sort();
    }
    return Array.from(new Set(teamMembers.map(m => m.status).filter(Boolean))).sort();
  }, [screenMode, directorySummary, teamMembers]);

  const types = useMemo(() => {
    if (screenMode === 'live') {
      return (directorySummary?.types ?? []).map(t => t.value).filter(Boolean).sort();
    }
    return Array.from(new Set(teamMembers.map(m => m.type).filter((t): t is string => Boolean(t)))).sort();
  }, [screenMode, directorySummary, teamMembers]);

  // Option lists for filter drawer
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: d, label: d })),
    [departments]
  );
  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: r, label: r })),
    [roles]
  );
  const statusOptions = useMemo(
    () => statuses.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) })),
    [statuses]
  );
  const typeOptions = useMemo(
    () => types.map((t) => ({ value: t, label: t })),
    [types]
  );

  const activeFilterCount = useMemo(() => countActiveTeamFilters(teamFilters), [teamFilters]);

  // Calculate statistics. Live mode uses the organisation-wide directory summary
  // because only one page of employees is loaded at a time.
  const stats = useMemo(() => {
    if (screenMode === 'live') {
      const total = directorySummary?.totalEmployees ?? 0;
      const active = (directorySummary?.statuses ?? [])
        .filter(s => normalizeStatus(s.value) === 'active')
        .reduce((sum, s) => sum + s.count, 0);
      const largest = [...(directorySummary?.departments ?? [])]
        .sort((a, b) => b.count - a.count)[0];

      return {
        total,
        active,
        inactive: total - active,
        largestDepartment: largest?.value || 'N/A',
        departmentCount: (directorySummary?.departments ?? []).filter(d => d.value).length,
      };
    }

    const total = teamMembers.length;
    const active = teamMembers.filter(m => m.status === 'active').length;
    const inactive = total - active;
    const departmentCounts = teamMembers.reduce((acc, m) => {
      acc[m.department] = (acc[m.department] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const largestDepartment = Object.entries(departmentCounts).sort((a, b) => b[1] - a[1])[0];

    return {
      total,
      active,
      inactive,
      largestDepartment: largestDepartment ? largestDepartment[0] : 'N/A',
      departmentCount: Object.keys(departmentCounts).length,
    };
  }, [screenMode, directorySummary, teamMembers]);

  // Handler for metric card clicks
  const handleMetricCardClick = (metric: 'total' | 'active' | 'inactive') => {
    const currentStatuses = teamFilters.statuses;
    const targetStatus = metric === 'total' ? '' : metric;

    if (metric === 'total') {
      setTeamFilters({ ...teamFilters, statuses: [] });
    } else if (currentStatuses.length === 1 && currentStatuses[0] === targetStatus) {
      setTeamFilters({ ...teamFilters, statuses: [] });
    } else {
      setTeamFilters({ ...teamFilters, statuses: [targetStatus] });
    }
  };

  // Filter and sort team members. In live mode the server already returns the
  // filtered, sorted page, so this is a passthrough of the server data.
  const filteredMembers = useMemo(() => {
    if (screenMode === 'live') {
      return teamMembers;
    }

    const filtered = teamMembers.filter(member => {
      // Search filter
      const matchesSearch = searchQuery === '' || 
        member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.department.toLowerCase().includes(searchQuery.toLowerCase());

      // Department filter
      const matchesDepartment = teamFilters.departments.length === 0 || teamFilters.departments.includes(member.department);

      // Role filter
      const matchesRole = teamFilters.roles.length === 0 || teamFilters.roles.includes(member.role);

      // Status filter
      const matchesStatus = teamFilters.statuses.length === 0 || teamFilters.statuses.includes(member.status);

      // Type filter
      const matchesType = teamFilters.types.length === 0 ||
        teamFilters.types.includes(member.type ?? 'Person');

      return matchesSearch && matchesDepartment && matchesRole && matchesStatus && matchesType;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'department':
          return a.department.localeCompare(b.department);
        case 'role':
          return a.role.localeCompare(b.role);
        case 'email':
          return a.email.localeCompare(b.email);
        default:
          return 0;
      }
    });

    return filtered;
  }, [screenMode, teamMembers, searchQuery, teamFilters, sortBy]);

  // Pagination metadata: server totals in live mode, the filtered demo set otherwise.
  const paginationMeta = useMemo(
    () =>
      calculatePagination(
        screenMode === 'live' ? totalApiMembers : filteredMembers.length,
        currentPage,
        pageSize
      ),
    [screenMode, totalApiMembers, filteredMembers.length, currentPage, pageSize]
  );

  // The members rendered by the grid and the list: the server page in live mode,
  // a client-side slice of the filtered demo data otherwise.
  const pageMembers = useMemo(() => {
    if (screenMode === 'live') {
      return filteredMembers;
    }
    return filteredMembers.slice(paginationMeta.startIndex, paginationMeta.endIndex);
  }, [screenMode, filteredMembers, paginationMeta.startIndex, paginationMeta.endIndex]);

  // Demo mode paginates locally, so keep the current page within range
  useEffect(() => {
    if (currentPage > paginationMeta.currentPage) {
      setPage(paginationMeta.currentPage);
    }
  }, [currentPage, paginationMeta.currentPage, setPage]);

  const hasSearchOrFilters = searchQuery.trim() !== '' || hasActiveTeamFilters(teamFilters);

  const handleViewDetails = useCallback((member: TeamMember) => {
    setSelectedMember(member);
    setShowDetailsDialog(true);
  }, []);

  const handleMemberUpdate = (updatedMember: TeamMember) => {
    // Update the member in the appropriate state based on screen mode
    if (screenMode === 'live') {
      setApiTeamMembers(prev => 
        prev.map(m => m.id === updatedMember.id ? updatedMember : m)
      );
    } else {
      // Update mock team members state
      setMockTeamMembers(prev => 
        prev.map(m => m.id === updatedMember.id ? updatedMember : m)
      );
    }
    // Update selected member to reflect changes in the dialog
    setSelectedMember(updatedMember);
  };

  // --- Bulk selection helpers ---
  const toggleMemberSelection = useCallback((id: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Select all applies to the employees on the current page only
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === pageMembers.length) return new Set();
      return new Set(pageMembers.map((m) => m.id));
    });
  }, [pageMembers]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setClearSelectionKey(k => k + 1);
  }, []);

  const handleTableSelectionChange = useCallback((rows: TeamMember[]) => {
    setSelectedIds(new Set(rows.map(r => r.id)));
  }, []);

  const selectedEmployees = useMemo(
    () => pageMembers.filter((m) => selectedIds.has(m.id)).map((m) => ({ id: String(m.id), name: m.name })),
    [pageMembers, selectedIds]
  );

  const getStatusColor = useCallback((status: 'active' | 'inactive' | 'on-leave') => {
    switch (status) {
      case 'active':
        return 'bg-success-500/10 text-success-700 dark:text-success-400 border-success-500/20';
      case 'on-leave':
        return 'bg-warning-500/10 text-warning-700 dark:text-warning-400 border-warning-500/20';
      default:
        return 'bg-neutral-500/10 text-neutral-700 dark:text-neutral-400 border-neutral-500/20';
    }
  }, []);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  // ── Table columns for list / table view ─────────────────────────────────
  const teamMemberColumns: ColumnDef<TeamMember>[] = useMemo(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
              ? 'indeterminate'
              : false
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'member',
      accessorKey: 'name',
      header: 'Member',
      cell: ({ row }) => {
        const member = row.original;
        return (
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative flex-shrink-0">
              <EmployeeImage name={member.name} photoUrl={member.photoUrl} size="sm" />
              {member.status === 'active' && (
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-success-500 rounded-full border-2 border-background" />
              )}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{member.name}</p>
              <p className="text-xs text-muted-foreground truncate">{member.employeeCode}</p>
            </div>
          </div>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const member = row.original;
        return (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn('text-xs', getStatusColor(member.status))}>
              <div className={cn(
                'w-1.5 h-1.5 rounded-full mr-1.5',
                member.status === 'active' ? 'bg-success-500' : 'bg-neutral-400'
              )} />
              {member.status}
            </Badge>
            {member.grade && (
              <EmployeeGradeBadge grade={member.grade} gradeLevel={member.gradeLevel} size="sm" showIcon={false} />
            )}
          </div>
        );
      },
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => (
        <span className="text-sm truncate max-w-[180px] block">{row.original.role}</span>
      ),
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'department',
      header: 'Department',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.department}</span>
      ),
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground truncate max-w-[200px] block">{row.original.email}</span>
      ),
      enableSorting: true,
    },
    {
      accessorKey: 'location',
      header: 'Location',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.location || '—'}</span>
      ),
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        row.original.type
          ? <Badge variant="outline" className="text-xs capitalize">{row.original.type}</Badge>
          : <span className="text-sm">—</span>
      ),
      enableSorting: true,
      enableGrouping: true,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const member = row.original;
        return canViewEmployeeDetails() ? (
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-info-50 hover:text-info-700 dark:hover:bg-info-950/30"
            onClick={(e) => { e.stopPropagation(); handleViewDetails(member); }}
          >
            <Eye className="h-4 w-4" />
          </Button>
        ) : null;
      },
      enableSorting: false,
    },
  ], [getStatusColor, canViewEmployeeDetails, handleViewDetails]);

  // Loading state — only for the first load, so paging does not blank the screen
  if (isLoading && teamMembers.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-10 w-72 mb-3" />
          <Skeleton className="h-5 w-96" />
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-36" />
          ))}
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <Skeleton key={i} className="h-80" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] space-y-4">
        <div className="rounded-full bg-error-100 dark:bg-error-900/20 p-4">
          <AlertCircle className="h-10 w-10 text-error-600 dark:text-error-400" />
        </div>
        <div className="text-center space-y-2">
          <h3 className="text-xl font-semibold">Failed to load team members</h3>
          <p className="text-sm text-muted-foreground max-w-md">{error}</p>
        </div>
        <Button onClick={() => window.location.reload()} size="lg">
          Try Again
        </Button>
      </div>
    );
  }

  // Access denied — viewDirectory permission is OFF
  if (!canViewEmployeeDirectory()) {
    return (
      <AccessRestricted message="You don't have permission to view the employee directory. Contact your administrator to request access." />
    );
  }

  return (
    <div className="space-y-6">
      {/* Modern Header */}
      <PageHeader
        title="Team Members"

        size={36}
        showActions={false}
        showBreadcrumb={false}
        icon={<Users />}
      />

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-tour="team-stats">
        <NavigationMetricCardAlt
          title="Total Employees"
          value={String(stats.total)}
          icon={Users}
          variant="info"
          change={`Across ${stats.departmentCount} departments`}
          onClick={() => handleMetricCardClick('total')}
          ariaLabel="Show all employees"
        />
        <NavigationMetricCardAlt
          title="Active"
          value={String(stats.active)}
          icon={UserCheck}
          variant="success"
          change={`${stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}% of total headcount`}
          onClick={() => handleMetricCardClick('active')}
          ariaLabel="Filter by active employees"
        />
        <NavigationMetricCardAlt
          title="Inactive"
          value={String(stats.inactive)}
          icon={UserX}
          variant={stats.inactive === 0 ? 'success' : stats.inactive <= 3 ? 'warning' : 'error'}
          change={`${stats.inactive} total inactive employee${stats.inactive !== 1 ? 's' : ''}`}
          onClick={() => handleMetricCardClick('inactive')}
          ariaLabel="Filter by inactive employees"
        />
        <NavigationMetricCardAlt
          title="Largest Department"
          value={stats.largestDepartment}
          icon={Building2}
          variant="info"
          change="Most team members"
        />
      </div>

      {/* Grid View — toolbar + cards wrapped in Card */}
      {viewMode === 'grid' && (
        <Card data-tour="team-search-filter">
          {/* Toolbar */}
          <div className="flex items-center gap-2 p-6 pb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, role, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {hasActiveTeamFilters(teamFilters) && (
                <Button variant="outline" onClick={() => setTeamFilters(EMPTY_TEAM_FILTERS)} className="text-muted-foreground">
                  <X className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
              )}
              <Button variant="outline" onClick={() => setFilterSheetOpen(true)} className="h-9 w-9 p-0 relative" aria-label="Filter">
                <Filter className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 text-[10px] font-semibold text-primary-foreground min-w-[18px] h-[18px] rounded-full bg-primary inline-flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
              <div className="flex gap-1 border rounded-md" data-tour="team-view-toggle">
                <Button variant="default" size="sm" onClick={() => setViewMode('grid')} className="rounded-r-none"><LayoutGrid className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => setViewMode('list')} className="rounded-l-none"><LayoutList className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
          {/* Grid content */}
          <CardContent className="pt-2 pb-6">
            {pageMembers.length === 0 && !hasSearchOrFilters && (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="rounded-full bg-muted p-4 mb-4">
                  <Users className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No team members found</h3>
                <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
                  There are no team members in the system yet.
                </p>
              </div>
            )}
            {pageMembers.length === 0 && hasSearchOrFilters && (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="rounded-full bg-muted p-4 mb-4">
                  <Filter className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No employees found</h3>
                <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
                  Try adjusting your search or filter criteria to find what you're looking for
                </p>
                <Button
                  onClick={() => {
                    setSearchQuery('');
                    setTeamFilters(EMPTY_TEAM_FILTERS);
                  }}
                >
                  Clear all filters
                </Button>
              </div>
            )}
            {pageMembers.length > 0 && (
              <>
                <div className="flex items-center justify-between px-1 mb-4">
                  <div className="flex items-center gap-3">
                    {pageMembers.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="select-all"
                          checked={selectedIds.size > 0 && selectedIds.size === pageMembers.length}
                          data-state={selectedIds.size > 0 && selectedIds.size < pageMembers.length ? 'indeterminate' : undefined}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Select all employees"
                        />
                        <label htmlFor="select-all" className="text-sm text-muted-foreground cursor-pointer select-none">
                          {selectedIds.size > 0
                            ? `${selectedIds.size} selected`
                            : 'Select all'}
                        </label>
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground font-medium">
                      Showing{' '}
                      <span className="text-foreground font-semibold">
                        {paginationMeta.startIndex + 1}-{paginationMeta.endIndex}
                      </span>{' '}
                      of {paginationMeta.totalItems} employees
                    </p>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-tour="team-member-grid">
              {pageMembers.map(member => (
                <Card 
                  key={member.id} 
                  className={cn(
                    "group border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden",
                    canViewEmployeeDetails() ? "cursor-pointer" : "cursor-default",
                    selectedIds.has(member.id) && "ring-2 ring-primary shadow-xl"
                  )}
                  onClick={() => canViewEmployeeDetails() && handleViewDetails(member)}
                >
                  {/* Employee Photo Header */}
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
                    <EmployeeCardImage
                      name={member.name}
                      photoUrl={member.photoUrl}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Employee Code Badge */}
                    <div className="absolute top-3 left-3 bg-slate-900/80 backdrop-blur-sm text-white px-2.5 py-1 rounded text-xs font-semibold">
                      {member.employeeCode}
                    </div>
                    {/* Selection checkbox — top-right, appears on hover or when selected */}
                    <div
                      className={cn(
                        'absolute top-2.5 right-2.5 transition-opacity',
                        selectedIds.has(member.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      )}
                      onClick={(e) => toggleMemberSelection(member.id, e)}
                    >
                      <Checkbox
                        checked={selectedIds.has(member.id)}
                        className="h-5 w-5 border-2 border-white bg-white/80 data-[state=checked]:bg-primary data-[state=checked]:border-primary shadow"
                        aria-label={`Select ${member.name}`}
                      />
                    </div>
                    {/* Status Indicator — bottom-right so it never overlaps the checkbox */}
                    {member.status === 'active' && (
                      <div className="absolute bottom-3 right-3 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white shadow-lg" />
                    )}
                  </div>
                  
                  <CardContent className="p-5">
                    {/* Name & Role Section */}
                    <div className="mb-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-semibold text-base group-hover:text-blue-600 transition-colors flex-1">
                          {member.name}
                        </h3>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{member.role}</p>
                      {member.grade && (
                        <div className="flex items-center gap-1.5">
                          <EmployeeGradeBadge grade={member.grade} gradeLevel={member.gradeLevel} size="sm" />
                        </div>
                      )}
                    </div>

                    {/* Info Grid */}
                    <div className="space-y-2.5 mb-4">
                      <div className="flex items-center gap-2.5 text-sm">
                        <div className="p-1.5 rounded-md bg-info-50 dark:bg-info-950/30">
                          <Briefcase className="h-3.5 w-3.5 text-info-600 dark:text-info-400" />
                        </div>
                        <span className="text-muted-foreground truncate">{member.role}</span>
                      </div>
                      <div className="flex items-center gap-2.5 text-sm">
                        <div className="p-1.5 rounded-md bg-purple-50 dark:bg-purple-950/30">
                          <Building2 className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <span className="text-muted-foreground truncate">{member.department}</span>
                      </div>
                      <div className="flex items-center gap-2.5 text-sm">
                        <div className="p-1.5 rounded-md bg-blueviolet-50 dark:bg-blueviolet-950/30">
                          <Mail className="h-3.5 w-3.5 text-blueviolet-600 dark:text-blueviolet-400" />
                        </div>
                        <span className="text-muted-foreground truncate text-xs">{member.email}</span>
                      </div>
                      {member.location && (
                        <div className="flex items-center gap-2.5 text-sm">
                          <div className="p-1.5 rounded-md bg-burntorange-50 dark:bg-burntorange-950/30">
                            <MapPin className="h-3.5 w-3.5 text-burntorange-600 dark:text-burntorange-400" />
                          </div>
                          <span className="text-muted-foreground truncate">{member.location}</span>
                        </div>
                      )}
                    </div>

                    {/* View Details Button — only shown when canViewEmployeeDetails() is true */}
                    {canViewEmployeeDetails() && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full group-hover:bg-info-50 group-hover:text-info-700 group-hover:border-info-200 dark:group-hover:bg-info-950/30 dark:group-hover:text-info-400 transition-all"
                      >
                        <Eye className="h-3.5 w-3.5 mr-2" />
                        View Details
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
                <PaginationControls
                  pagination={paginationMeta}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  className="mt-6 px-1"
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* List View — toolbar + table wrapped in Card */}
      {viewMode === 'list' && (
        <Card data-tour="team-search-filter">
          {/* Toolbar */}
          <div className="flex items-center gap-2 p-6 pb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, role, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {hasActiveTeamFilters(teamFilters) && (
                <Button variant="outline" onClick={() => setTeamFilters(EMPTY_TEAM_FILTERS)} className="text-muted-foreground">
                  <X className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
              )}
              <Button variant="outline" onClick={() => setFilterSheetOpen(true)} className="h-9 w-9 p-0 relative" aria-label="Filter">
                <Filter className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 text-[10px] font-semibold text-primary-foreground min-w-[18px] h-[18px] rounded-full bg-primary inline-flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const btn = document.querySelector<HTMLButtonElement>('[data-field-selector-trigger]');
                  if (btn) btn.click();
                }}
                className="gap-2"
              >
                <Settings2 className="h-4 w-4" />
                Columns
              </Button>
              <div className="flex gap-1 border rounded-md" data-tour="team-view-toggle">
                <Button variant="ghost" size="sm" onClick={() => setViewMode('grid')} className="rounded-r-none"><LayoutGrid className="h-4 w-4" /></Button>
                <Button variant="default" size="sm" onClick={() => setViewMode('list')} className="rounded-l-none"><LayoutList className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
          {/* Table */}
          <CardContent className="p-0">
            {pageMembers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 border-t">
                <div className="rounded-full bg-muted p-4 mb-4">
                  {!hasSearchOrFilters ? (
                    <Users className="h-10 w-10 text-muted-foreground" />
                  ) : (
                    <Filter className="h-10 w-10 text-muted-foreground" />
                  )}
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  {!hasSearchOrFilters ? 'No team members found' : 'No employees found'}
                </h3>
                <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
                  {!hasSearchOrFilters
                    ? 'There are no team members in the system yet.'
                    : 'Try adjusting your search or filter criteria to find what you\'re looking for'}
                </p>
                {hasSearchOrFilters && (
                  <Button
                    onClick={() => {
                      setSearchQuery('');
                      setTeamFilters(EMPTY_TEAM_FILTERS);
                    }}
                  >
                    Clear all filters
                  </Button>
                )}
              </div>
            ) : (
              <>
              <p className="text-sm text-muted-foreground font-medium px-6 pb-3">
                Showing{' '}
                <span className="text-foreground font-semibold">
                  {paginationMeta.startIndex + 1}-{paginationMeta.endIndex}
                </span>{' '}
                of {paginationMeta.totalItems} employees
              </p>
              <DataTableEnhanced
                key={clearSelectionKey}
                columns={teamMemberColumns}
                data={pageMembers}
                enableGrouping={false}
                enableColumnVisibility={true}
                enableSorting={true}
                enableFiltering={false}
                enableRowSelection={true}
                pageSize={pageSize}
                hidePagination
                onRowClick={canViewEmployeeDetails() ? handleViewDetails : undefined}
                storageKey="team-members-table"
                hideFieldSelector={false}
                hideFieldSelectorTrigger={true}
                fieldLabels={{
                  name: 'Member',
                  status: 'Status',
                  role: 'Role',
                  department: 'Department',
                  email: 'Email',
                  location: 'Location',
                }}
                onSelectionChange={handleTableSelectionChange}
              />
              <PaginationControls
                pagination={paginationMeta}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                className="px-6 pb-6"
              />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter Drawer */}
      <TeamMembersFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        appliedFilters={teamFilters}
        onApply={setTeamFilters}
        onClear={() => setTeamFilters(EMPTY_TEAM_FILTERS)}
        availableOptions={{
          departments: departmentOptions,
          roles: roleOptions,
          statuses: statusOptions,
          types: typeOptions,
        }}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />

      {/* Details Dialog */}
      <TeamMemberDetailsDialog
        member={selectedMember}
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        onMemberUpdate={handleMemberUpdate}
        getStatusColor={getStatusColor}
        formatDate={formatDate}
      />

      {/* Bulk Selection Bar — fixed bottom teal bar */}
      <BulkSelectionBar
        selectedCount={selectedIds.size}
        onClear={clearSelection}
        onBulkUpdate={(tab) => {
          setBulkUpdateInitialTab(tab);
          setShowBulkUpdateDialog(true);
        }}
        canManageCompanyAccess={canBulkManageCompanyAccess}
        canManageApprovalWorkflow={canBulkManageApprovalWorkflow}
      />

      {/* Bulk Update Dialog */}
      <BulkUpdateDialog
        open={showBulkUpdateDialog}
        onOpenChange={setShowBulkUpdateDialog}
        selectedEmployees={selectedEmployees}
        canManageCompanyAccess={canBulkManageCompanyAccess}
        canManageApprovalWorkflow={canBulkManageApprovalWorkflow}
        initialTab={bulkUpdateInitialTab}
      />

      {/* Bottom padding so content isn't hidden behind the sticky bar when items are selected */}
      {selectedIds.size > 0 && <div className="h-16" />}
    </div>
  );
};
